import type {
  ComboboxOption,
  ComboboxOptions
} from '@library/components/selects';
import {
  useAddMemberGroupMemberMutation,
  useGetMemberGroupsOfMemberQuery,
  useRemoveMemberGroupMemberMutation
} from '@bo-services/members/memberGroupsApi';
import { useCallback, useMemo, useState } from 'react';
import { useMemberGroups } from '@bo-hooks/memberGroups/useMemberGroups';

export interface MemberManageGroupsActionsProps {
  memberId: string;
}

export const useMemberManageGroupActions = ({
  memberId
}: MemberManageGroupsActionsProps) => {
  const [confirmRemoveGroupId, setConfirmRemoveGroupId] = useState<
    string | null
  >(null);

  const { memberGroups: allMemberGroups } = useMemberGroups();

  const {
    data: memberGroupsData,
    isFetching: isFetchingActiveGroups,
    isLoading: isLoadingActiveGroups
  } = useGetMemberGroupsOfMemberQuery({
    id: Number(memberId)
  });

  const [addMemberToGroup, { isLoading: isLoadingAddGroups }] =
    useAddMemberGroupMemberMutation();
  const [removeMemberFromGroup, { isLoading: isLoadingRemoveGroups }] =
    useRemoveMemberGroupMemberMutation();

  // Combobox options to add a member to a group
  const addMemberGroupOptions = useMemo(
    () =>
      allMemberGroups
        ?.filter(
          ({ id }) => !memberGroupsData?.some((group) => group.id === id)
        )
        .map(({ id, name }) => ({
          label: name,
          value: id
        })) as ComboboxOptions,
    [memberGroupsData, allMemberGroups]
  );

  // Groups assigned to the member
  const assignedMemberGroups = useMemo(() => {
    setConfirmRemoveGroupId(null);

    return memberGroupsData?.map(({ id, name }) => ({
      id: String(id),
      name: String(name)
    }));
  }, [memberGroupsData]);

  const handleShowConfirmIcons = useCallback(
    (id: string) => () => {
      setConfirmRemoveGroupId(id);
    },
    [setConfirmRemoveGroupId]
  );

  const handleHideConfirmIcons = useCallback(() => {
    setConfirmRemoveGroupId(null);
  }, [setConfirmRemoveGroupId]);

  const handleAddGroup = useCallback(
    async (option: ComboboxOption | null) => {
      const groupId = String(option?.value);

      if (groupId) {
        await addMemberToGroup({
          id: groupId,
          user_id: memberId
        });
      }
    },
    [addMemberToGroup, memberId]
  );

  const handleRemoveGroup = useCallback(
    (id?: string) => async () => {
      if (id) {
        await removeMemberFromGroup({
          id,
          user_id: memberId
        });
      }
    },
    [memberId, removeMemberFromGroup]
  );

  const isLoadingAddRemoveGroups = isLoadingAddGroups || isLoadingRemoveGroups;
  const showSpinner = isFetchingActiveGroups || isLoadingAddRemoveGroups;

  return {
    addMemberGroupOptions,
    assignedMemberGroups,
    confirmRemoveGroupId,
    isLoadingActiveGroups,
    isLoadingAddRemoveGroups,
    onAddGroup: handleAddGroup,
    onHideConfirmIcons: handleHideConfirmIcons,
    onRemoveGroup: handleRemoveGroup,
    onShowConfirmIcons: handleShowConfirmIcons,
    showSpinner
  };
};
